import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout';
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, AlertCircle, Download, History, RefreshCw, Eye, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { apiClient } from '../api/client';

interface ParsedRow {
  ticketId: string;
  email: string;
  ideaTitleInput: string;
}

interface AssigneeImportLog {
  id: string;
  totalCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  importType?: string | null;
  createdAt: string;
  importedBy?: { id: string; name: string; email: string; role: string };
}

interface AssigneeImportLogRow {
  id: string;
  rowNum: number;
  ticketId: string;
  ideaName: string;
  assigneeEmail?: string | null;
  status: string;
  reason: string;
}

interface AssigneeImportLogDetails extends AssigneeImportLog {
  rows: AssigneeImportLogRow[];
}

interface PreviewRow extends ParsedRow {
  realIdeaId: string | null;
  ideaTitle: string | null;
  ideaFound: boolean;
  ideaTitleMatch: boolean;
  userFound?: boolean;
  userName?: string | null;
  userRole?: string | null;
  isValid?: boolean;
  skipReason?: string;
}

export function ImportBulkAssigneePage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'NEW_IMPORT' | 'HISTORY'>('NEW_IMPORT');
  const [selectedHistoryLogId, setSelectedHistoryLogId] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);

  const [resultMessage, setResultMessage] = useState<{ type: 'success' | 'error', message: string, details?: any } | null>(null);

  const { data: historyLogsData, isLoading: historyLoading, refetch: refetchHistory } = useQuery({
    queryKey: ['bulkAssigneeImportLogs'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/ideas/bulk-import-logs');
      return res.data.data as AssigneeImportLog[];
    },
  });

  const assigneeHistoryLogs = (historyLogsData || []).filter((log) => log.importType === 'BULK_ASSIGNEE');

  const { data: historyLogDetailsData, isLoading: detailsLoading } = useQuery({
    queryKey: ['bulkAssigneeImportLogDetails', selectedHistoryLogId],
    queryFn: async () => {
      if (!selectedHistoryLogId) return null;
      const res = await apiClient.get(`/admin/ideas/bulk-import-logs/${selectedHistoryLogId}`);
      return res.data.data as AssigneeImportLogDetails;
    },
    enabled: !!selectedHistoryLogId,
  });

  const downloadSampleFormat = () => {
    const ws = XLSX.utils.json_to_sheet([{
      'Ticket ID': 'CG12345',
      'Name of idea': 'Cost saving in manufacturing process',
      'Allocate Mail ID': 'john.doe@example.com'
    }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sample');
    XLSX.writeFile(wb, 'Bulk_Assignee_Sample.xlsx');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setParsedData([]);
      setPreviewData([]);
      setResultMessage(null);
    }
  };

  const handleUploadAndPreview = () => {
    if (!file) return;

    setIsParsing(true);
    setResultMessage(null);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws) as any[];

        const extractedData: ParsedRow[] = [];

        for (const row of data) {
          const ticketId = row['Ticket ID'] || row['ticket id'] || row['Ticket Id'] || row['ticketId'];
          const email = row['Allocate Mail ID'] || row['allocate mail id'] || row['Allocate mail id'] || row['Email'] || row['email'] || row['Email ID'];
          const ideaTitleInput = row['Name of idea'] || row['Name of Idea'] || row['name of idea'] || row['Idea Name'] || row['Title'];

          if (ticketId && email && ideaTitleInput) {
            extractedData.push({
              ticketId: ticketId.toString().trim(),
              email: email.toString().trim(),
              ideaTitleInput: ideaTitleInput.toString().trim()
            });
          }
        }

        if (extractedData.length === 0) {
          setResultMessage({ type: 'error', message: 'No valid data found. Make sure columns "Ticket ID", "Name of idea", and "Allocate Mail ID" exist.' });
          setIsParsing(false);
          return;
        }

        setParsedData(extractedData);

        // Fetch Preview
        setIsPreviewing(true);
        try {
          const res = await apiClient.post('/admin/bulk-assign/preview', { assignments: extractedData });
          setPreviewData(res.data.data);
        } catch (error: any) {
          setResultMessage({ type: 'error', message: error.response?.data?.error?.message || 'Failed to fetch preview' });
        } finally {
          setIsPreviewing(false);
        }

      } catch (error) {
        setResultMessage({ type: 'error', message: 'Error parsing Excel file' });
      } finally {
        setIsParsing(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleSubmit = async () => {
    if (previewData.length === 0) return;

    setIsSubmitting(true);
    setResultMessage(null);

    try {
      const res = await apiClient.post('/admin/bulk-assign/execute', { assignments: parsedData });

      const { assignedCount, skippedCount, skippedDetails } = res.data.data;

      setResultMessage({
        type: 'success',
        message: `Successfully assigned ${assignedCount} users. Skipped ${skippedCount} users.`,
        details: skippedDetails
      });

      // Clear current state
      queryClient.invalidateQueries({ queryKey: ['bulkAssigneeImportLogs'] });
      setFile(null);
      setParsedData([]);
      setPreviewData([]);
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

    } catch (error: any) {
      setResultMessage({ type: 'error', message: error.response?.data?.error?.message || 'Failed to execute assignments' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout title="Import Bulk Assignee">
      <div className="max-w-6xl mx-auto space-y-6 pb-12">
        <div className="flex border-b border-slate-200 mb-2 bg-slate-50/50 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => setActiveTab('NEW_IMPORT')}
            className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${activeTab === 'NEW_IMPORT' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'}`}
          >
            <Upload className="w-4 h-4" />
            <span>New Import Upload</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('HISTORY');
              refetchHistory();
            }}
            className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${activeTab === 'HISTORY' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'}`}
          >
            <History className="w-4 h-4" />
            <span>Import History Logs ({assigneeHistoryLogs.length})</span>
          </button>
        </div>

        {activeTab === 'NEW_IMPORT' && (
        <>
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-start space-x-4">
            <div className="bg-blue-100 p-3 rounded-xl text-blue-700">
              <FileSpreadsheet className="w-8 h-8" />
            </div>
            <div>


              <h1 className="text-2xl font-bold text-slate-800">Import Bulk Assignees</h1>
              <p className="text-slate-500 mt-1">
                Upload an Excel file to assign users to ideas in bulk.
                The file must contain headers: <span className="font-bold text-slate-700">Ticket ID</span>, <span className="font-bold text-slate-700">Name of idea</span>, and <span className="font-bold text-slate-700">Allocate Mail ID</span>.
              </p>
            </div>
          </div>

          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start space-x-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800">Important Requirements</h3>
              <div className="text-sm text-amber-700 mt-1 space-y-1">
                <p>1. Both <strong>Ticket ID</strong> AND <strong>Name of idea</strong> in the Excel sheet <strong>must match</strong> the actual Ticket ID and Idea Title in the database.</p>
                <p>2. The employee you are assigning <strong>must be a regular Committee Member</strong> (Leaders, Committee Heads, and Executors will be <strong>skipped</strong>).</p>
                <p>3. <strong>Single Assignee Enforcement:</strong> Each idea receives only 1 primary Committee Member. Uploading a new user will set them as the single evaluator.</p>
                <p className="mt-1 text-amber-900 font-medium">If the Ticket ID is not found, Idea Name mismatches, email is invalid, or the user holds a non-evaluator role, the row will be <strong>skipped with an explicit reason in preview</strong>.</p>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-slate-700">Upload Excel File (.xlsx, .xls)</label>
              <button onClick={downloadSampleFormat} className="text-blue-600 hover:text-blue-700 text-sm font-semibold flex items-center space-x-1.5 hover:underline transition-all">
                <Download className="w-4 h-4" />
                <span>Download Sample Format</span>
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <input
                id="file-upload"
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-slate-200 rounded-full"
              />
              <button
                onClick={handleUploadAndPreview}
                disabled={!file || isParsing || isPreviewing}
                className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
              >
                {isParsing || isPreviewing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Upload className="w-5 h-5" />
                )}
                <span>Preview Data</span>
              </button>
            </div>
          </div>
        </div>

        {resultMessage && (
          <div className={`p-4 rounded-xl border flex items-start space-x-3 ${resultMessage.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            {resultMessage.type === 'success' ? <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" /> : <AlertCircle className="w-6 h-6 shrink-0 mt-0.5" />}
            <div className="flex-1">
              <h3 className="font-bold">{resultMessage.type === 'success' ? 'Success' : 'Error'}</h3>
              <p className="text-sm mt-1">{resultMessage.message}</p>

              {resultMessage.details && resultMessage.details.length > 0 && (
                <div className="mt-3 bg-white bg-opacity-50 rounded-lg p-3 text-xs max-h-40 overflow-y-auto border border-current opacity-80">
                  <p className="font-bold mb-2">Skipped Items Details:</p>
                  <ul className="list-disc pl-4 space-y-1">
                    {resultMessage.details.map((d: any, idx: number) => (
                      <li key={idx}><strong>{d.email}</strong> on Idea {d.ticketId} — <span className="font-semibold text-red-700">{d.reason}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {previewData.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4">
            <div className="bg-slate-50 border-b border-slate-200 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Preview Assignments</h2>
                <p className="text-sm text-slate-500 mt-1">Verify ideas, emails, and roles before confirming.</p>
              </div>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-6 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                <span>Confirm & Assign</span>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="px-6 py-3 font-semibold w-1/4">Ticket ID</th>
                    <th className="px-6 py-3 font-semibold w-1/4">Name of idea</th>
                    <th className="px-6 py-3 font-semibold w-1/4">Allocate Mail ID</th>
                    <th className="px-6 py-4 font-semibold w-1/4">Status & Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {previewData.map((row, idx) => {
                    const isRowValid = row.isValid !== undefined ? row.isValid : (row.ideaFound && row.ideaTitleMatch);
                    return (
                      <tr key={idx} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-slate-800">{row.ticketId}</span>
                            {!row.ideaFound && (
                              <span className="bg-red-100 text-red-700 text-[10px] px-2 py-0.5 rounded font-semibold whitespace-nowrap">Idea Not Found</span>
                            )}
                          </div>
                          {row.ideaTitle && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1" title={row.ideaTitle}>{row.ideaTitle}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col space-y-1">
                            <span className="font-medium text-slate-800 line-clamp-1" title={row.ideaTitleInput}>{row.ideaTitleInput}</span>
                            {row.ideaFound && (
                              row.ideaTitleMatch ? (
                                <span className="w-fit bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded font-semibold whitespace-nowrap">Matched</span>
                              ) : (
                                <span className="w-fit bg-red-100 text-red-700 text-[10px] px-2 py-0.5 rounded font-semibold whitespace-nowrap">Mismatch</span>
                              )
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-700">{row.email}</div>
                          {row.userName && <div className="text-xs text-slate-500 font-normal">{row.userName}</div>}
                        </td>
                        <td className="px-6 py-4">
                          {isRowValid ? (
                            <div className="flex flex-col space-y-1">
                              <span className="w-fit text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full text-xs font-bold">
                                Ready to Assign
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col space-y-1">
                              <span className="w-fit text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full text-xs font-bold">
                                Will be Skipped
                              </span>
                              {row.skipReason && (
                                <span className="text-[11px] font-semibold text-red-600">
                                  {row.skipReason}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}

        {activeTab === 'HISTORY' && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <History className="w-4 h-4 text-blue-600" />
                <span>Past Assignee Upload History</span>
              </h3>
              <button
                type="button"
                onClick={() => refetchHistory()}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 cursor-pointer"
                title="Refresh History"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {historyLoading ? (
              <div className="py-12 text-center text-slate-400 flex flex-col items-center space-y-2">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span className="text-xs">Loading import history logs...</span>
              </div>
            ) : assigneeHistoryLogs.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-xs">
                No assignee import history found.
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-700 font-bold uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="p-3.5">Import Date & Time</th>
                      <th className="p-3.5">Imported By</th>
                      <th className="p-3.5 text-center">Total</th>
                      <th className="p-3.5 text-center">Success</th>
                      <th className="p-3.5 text-center">Failed</th>
                      <th className="p-3.5 text-center">Skipped</th>
                      <th className="p-3.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                    {assigneeHistoryLogs.map((logSession) => (
                      <tr key={logSession.id} className="hover:bg-slate-50">
                        <td className="p-3.5 font-bold text-slate-800">
                          {new Date(logSession.createdAt).toLocaleString()}
                        </td>
                        <td className="p-3.5">
                          <div className="font-semibold text-slate-900">{logSession.importedBy?.name || 'N/A'}</div>
                          <div className="text-[10px] text-slate-400">{logSession.importedBy?.email}</div>
                        </td>
                        <td className="p-3.5 text-center font-bold text-slate-800">{logSession.totalCount}</td>
                        <td className="p-3.5 text-center font-bold text-emerald-600">{logSession.successCount}</td>
                        <td className="p-3.5 text-center font-bold text-red-600">{logSession.failedCount}</td>
                        <td className="p-3.5 text-center font-bold text-slate-500">{logSession.skippedCount}</td>
                        <td className="p-3.5 text-right">
                          <button
                            type="button"
                            onClick={() => setSelectedHistoryLogId(logSession.id)}
                            className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-blue-600 font-bold rounded-lg text-xs transition-colors inline-flex items-center space-x-1 cursor-pointer"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>View Details</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {selectedHistoryLogId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full p-6 shadow-2xl space-y-4 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                  <span>Import Session Details Log</span>
                </h3>
                <button
                  type="button"
                  onClick={() => setSelectedHistoryLogId(null)}
                  className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {detailsLoading ? (
                <div className="py-16 text-center text-slate-400 flex flex-col items-center space-y-2">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="text-xs">Fetching import row details...</span>
                </div>
              ) : historyLogDetailsData ? (
                <div className="space-y-4 overflow-y-auto pr-1 flex-1">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-3 rounded-xl text-xs">
                    <div>
                      <span className="text-slate-400 text-[10px] uppercase font-bold block">Imported By</span>
                      <span className="font-bold text-slate-800">{historyLogDetailsData.importedBy?.name}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 text-[10px] uppercase font-bold block">Date & Time</span>
                      <span className="font-semibold text-slate-700">{new Date(historyLogDetailsData.createdAt).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 text-[10px] uppercase font-bold block">Total Rows</span>
                      <span className="font-bold text-slate-800">{historyLogDetailsData.totalCount}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 text-[10px] uppercase font-bold block">Status Breakdown</span>
                      <span className="font-semibold text-emerald-600">{historyLogDetailsData.successCount} Success</span>
                      {' '}
                      <span className="font-semibold text-slate-500">{historyLogDetailsData.skippedCount} Skipped</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-slate-700 font-bold uppercase tracking-wider border-b border-slate-200">
                        <tr>
                          <th className="p-3">Row</th>
                          <th className="p-3">Ticket ID</th>
                          <th className="p-3">Name of idea</th>
                          <th className="p-3">Allocate Mail ID</th>
                          <th className="p-3">Status</th>
                          <th className="p-3">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(historyLogDetailsData.rows || []).map((row) => (
                          <tr key={row.id} className="hover:bg-slate-50">
                            <td className="p-3 font-mono text-slate-500">{row.rowNum}</td>
                            <td className="p-3 font-bold text-blue-700">{row.ticketId}</td>
                            <td className="p-3 font-semibold text-slate-800">{row.ideaName}</td>
                            <td className="p-3 text-slate-600">{row.assigneeEmail || '-'}</td>
                            <td className="p-3">
                              <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${row.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                {row.status}
                              </span>
                            </td>
                            <td className="p-3 text-slate-600">{row.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center text-slate-400 text-xs">Import log not found.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
